import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import docusealService from '../services/docusealService';
import './SigningSetupModal.css';
import { Document, Page, pdfjs } from 'react-pdf';
import { Rnd } from 'react-rnd';
import debounce from 'lodash.debounce';

// Use an ESM worker served from public/ that matches installed pdfjs-dist (v5 uses .mjs worker)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

export default function SigningSetupModal({ 
  document: documentData, 
  recipients, 
  isOpen, 
  onClose
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [fieldType, setFieldType] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageMeta, setPageMeta] = useState({}); // { [pageNumber]: { origWidth, origHeight } }
  const [zoom, setZoom] = useState(1); // 1 = fit width
  const pdfContainerRef = useRef(null);

  // Field types available for placement - using DocuSeal API field types
  const fieldTypes = [
          { value: 'signature', label: 'Signature', icon: 'Signature' },
      { value: 'text', label: 'Text Field', icon: 'Text' },
        { value: 'date', label: 'Date', icon: 'Date' },
      { value: 'initials', label: 'Initials', icon: 'Initials' },
      { value: 'checkbox', label: 'Checkbox', icon: 'Checkbox' }
  ];


  // MASSIVE color palette for different recipients/roles (60+ colors!)
  const recipientColors = [
    // Primary colors
    { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', hover: '#2563eb', name: 'Blue' },
    { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', hover: '#dc2626', name: 'Red' },
    { border: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', hover: '#059669', name: 'Green' },
    { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', hover: '#d97706', name: 'Orange' },
    { border: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', hover: '#7c3aed', name: 'Purple' },
    { border: '#06b6d4', bg: 'rgba(6, 182, 212, 0.1)', hover: '#0891b2', name: 'Cyan' },
    { border: '#ec4899', bg: 'rgba(236, 72, 153, 0.1)', hover: '#db2777', name: 'Pink' },
    { border: '#84cc16', bg: 'rgba(132, 204, 22, 0.1)', hover: '#65a30d', name: 'Lime' },
    { border: '#f97316', bg: 'rgba(249, 115, 22, 0.1)', hover: '#ea580c', name: 'Orange Red' },
    { border: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)', hover: '#4f46e5', name: 'Indigo' },
    
    // Extended palette
    { border: '#14b8a6', bg: 'rgba(20, 184, 166, 0.1)', hover: '#0d9488', name: 'Teal' },
    { border: '#f43f5e', bg: 'rgba(244, 63, 94, 0.1)', hover: '#e11d48', name: 'Rose' },
    { border: '#a855f7', bg: 'rgba(168, 85, 247, 0.1)', hover: '#9333ea', name: 'Violet' },
    { border: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)', hover: '#16a34a', name: 'Emerald' },
    { border: '#eab308', bg: 'rgba(234, 179, 8, 0.1)', hover: '#ca8a04', name: 'Yellow' },
    { border: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.1)', hover: '#0284c7', name: 'Sky' },
    { border: '#d946ef', bg: 'rgba(217, 70, 239, 0.1)', hover: '#c026d3', name: 'Fuchsia' },
    { border: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', hover: '#475569', name: 'Slate' },
    { border: '#f97316', bg: 'rgba(249, 115, 22, 0.1)', hover: '#ea580c', name: 'Orange' },
    { border: '#06b6d4', bg: 'rgba(6, 182, 212, 0.1)', hover: '#0891b2', name: 'Aqua' },
    
    // Dark variants
    { border: '#dc2626', bg: 'rgba(220, 38, 38, 0.1)', hover: '#b91c1c', name: 'Red Dark' },
    { border: '#16a34a', bg: 'rgba(22, 163, 74, 0.1)', hover: '#15803d', name: 'Green Dark' },
    { border: '#ca8a04', bg: 'rgba(202, 138, 4, 0.1)', hover: '#a16207', name: 'Amber' },
    { border: '#0891b2', bg: 'rgba(8, 145, 178, 0.1)', hover: '#0e7490', name: 'Cyan Dark' },
    { border: '#9333ea', bg: 'rgba(147, 51, 234, 0.1)', hover: '#7c2d12', name: 'Purple Dark' },
    { border: '#be123c', bg: 'rgba(190, 18, 60, 0.1)', hover: '#9f1239', name: 'Rose Dark' },
    { border: '#059669', bg: 'rgba(5, 150, 105, 0.1)', hover: '#047857', name: 'Emerald Dark' },
    { border: '#ea580c', bg: 'rgba(234, 88, 12, 0.1)', hover: '#c2410c', name: 'Orange Dark' },
    { border: '#0d9488', bg: 'rgba(13, 148, 136, 0.1)', hover: '#0f766e', name: 'Teal Dark' },
    { border: '#7c3aed', bg: 'rgba(124, 58, 237, 0.1)', hover: '#6d28d9', name: 'Violet Dark' },
    
    // More unique colors
    { border: '#65a30d', bg: 'rgba(101, 163, 13, 0.1)', hover: '#4d7c0f', name: 'Lime Dark' },
    { border: '#db2777', bg: 'rgba(219, 39, 119, 0.1)', hover: '#be185d', name: 'Pink Dark' },
    { border: '#2563eb', bg: 'rgba(37, 99, 235, 0.1)', hover: '#1d4ed8', name: 'Blue Dark' },
    { border: '#dc2626', bg: 'rgba(220, 38, 38, 0.1)', hover: '#b91c1c', name: 'Crimson' },
    { border: '#059669', bg: 'rgba(5, 150, 105, 0.1)', hover: '#047857', name: 'Forest' },
    { border: '#7c2d12', bg: 'rgba(124, 45, 18, 0.1)', hover: '#991b1b', name: 'Brown' },
    { border: '#1e40af', bg: 'rgba(30, 64, 175, 0.1)', hover: '#1e3a8a', name: 'Navy' },
    { border: '#be185d', bg: 'rgba(190, 24, 93, 0.1)', hover: '#9d174d', name: 'Magenta' },
    { border: '#0f766e', bg: 'rgba(15, 118, 110, 0.1)', hover: '#115e59', name: 'Mint' },
    { border: '#92400e', bg: 'rgba(146, 64, 14, 0.1)', hover: '#78350f', name: 'Bronze' },
    
    // EVEN MORE COLORS! 🌈
    { border: '#e11d48', bg: 'rgba(225, 29, 72, 0.1)', hover: '#be123c', name: 'Rose 2' },
    { border: '#0f766e', bg: 'rgba(15, 118, 110, 0.1)', hover: '#0f766e', name: 'Teal 2' },
    { border: '#7c2d12', bg: 'rgba(124, 45, 18, 0.1)', hover: '#7c2d12', name: 'Brown 2' },
    { border: '#1e3a8a', bg: 'rgba(30, 58, 138, 0.1)', hover: '#1e40af', name: 'Blue 2' },
    { border: '#166534', bg: 'rgba(22, 101, 52, 0.1)', hover: '#16a34a', name: 'Green 2' },
    { border: '#9f1239', bg: 'rgba(159, 18, 57, 0.1)', hover: '#be123c', name: 'Rose 3' },
    { border: '#0c4a6e', bg: 'rgba(12, 74, 110, 0.1)', hover: '#0284c7', name: 'Sky 2' },
    { border: '#7c2d12', bg: 'rgba(124, 45, 18, 0.1)', hover: '#7c2d12', name: 'Orange 2' },
    { border: '#581c87', bg: 'rgba(88, 28, 135, 0.1)', hover: '#7c3aed', name: 'Purple 2' },
    { border: '#064e3b', bg: 'rgba(6, 78, 59, 0.1)', hover: '#059669', name: 'Emerald 2' },
    
    // Vibrant extras
    { border: '#dc2626', bg: 'rgba(220, 38, 38, 0.1)', hover: '#dc2626', name: 'Red 2' },
    { border: '#16a34a', bg: 'rgba(22, 163, 74, 0.1)', hover: '#16a34a', name: 'Green 3' },
    { border: '#ca8a04', bg: 'rgba(202, 138, 4, 0.1)', hover: '#ca8a04', name: 'Yellow 2' },
    { border: '#0891b2', bg: 'rgba(8, 145, 178, 0.1)', hover: '#0891b2', name: 'Cyan 2' },
    { border: '#9333ea', bg: 'rgba(147, 51, 234, 0.1)', hover: '#9333ea', name: 'Purple 3' },
    { border: '#be123c', bg: 'rgba(190, 18, 60, 0.1)', hover: '#be123c', name: 'Pink 2' },
    { border: '#059669', bg: 'rgba(5, 150, 105, 0.1)', hover: '#059669', name: 'Teal 3' },
    { border: '#ea580c', bg: 'rgba(234, 88, 12, 0.1)', hover: '#ea580c', name: 'Orange 3' },
    { border: '#0d9488', bg: 'rgba(13, 148, 136, 0.1)', hover: '#0d9488', name: 'Aqua 2' },
    { border: '#7c3aed', bg: 'rgba(124, 58, 237, 0.1)', hover: '#7c3aed', name: 'Violet 2' },
    
    // Final batch for maximum variety
    { border: '#65a30d', bg: 'rgba(101, 163, 13, 0.1)', hover: '#65a30d', name: 'Lime 2' },
    { border: '#db2777', bg: 'rgba(219, 39, 119, 0.1)', hover: '#db2777', name: 'Pink 3' },
    { border: '#2563eb', bg: 'rgba(37, 99, 235, 0.1)', hover: '#2563eb', name: 'Blue 3' },
    { border: '#dc2626', bg: 'rgba(220, 38, 38, 0.1)', hover: '#dc2626', name: 'Crimson 2' },
    { border: '#059669', bg: 'rgba(5, 150, 105, 0.1)', hover: '#059669', name: 'Forest 2' },
    { border: '#7c2d12', bg: 'rgba(124, 45, 18, 0.1)', hover: '#7c2d12', name: 'Brown 3' },
    { border: '#1e40af', bg: 'rgba(30, 64, 175, 0.1)', hover: '#1e40af', name: 'Navy 2' },
    { border: '#be185d', bg: 'rgba(190, 24, 93, 0.1)', hover: '#be185d', name: 'Magenta 2' },
    { border: '#0f766e', bg: 'rgba(15, 118, 110, 0.1)', hover: '#0f766e', name: 'Mint 2' },
    { border: '#92400e', bg: 'rgba(146, 64, 14, 0.1)', hover: '#92400e', name: 'Bronze 2' }
  ];

  // Function to get color for a recipient
  const getRecipientColor = (recipientId) => {
    if (!recipientId) return recipientColors[0]; // Default to blue
    // Generate consistent color based on recipient ID
    let hash = 0;
    for (let i = 0; i < recipientId.length; i++) {
      hash = recipientId.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash) % recipientColors.length;
    const color = recipientColors[colorIndex];
    
    // Debug logging
    console.log(`🎨 Color for recipient ${recipientId}:`, {
      recipientId,
      hash,
      colorIndex,
      color: color.name
    });
    
    return color;
  };

  useEffect(() => {
    if (isOpen && documentData?.fileURL) {
      console.log('🔗 Setting PDF URL:', documentData.fileURL);
      console.log('📄 Document data:', documentData);
      console.log('👥 Recipients:', recipients);
      console.log('👥 Recipients details:', recipients?.map(r => ({
        id: r.id,
        name: r.name,
        email: r.email,
        isPM: r.isPM
      })));
      setPdfUrl(documentData.fileURL);
    } else if (!isOpen) {
      // Reset state when modal closes, but delay to prevent immediate recalculations
      setTimeout(() => {
        // setContainerWidth(0);
        setNumPages(0);
        setPageMeta({});
        setZoom(1); // Reset zoom to 1
        console.log('🔄 Modal closed, state reset');
      }, 100);
    }
  }, [isOpen, documentData, recipients]);

  // Helpers: compute per-page scale based on fixed width and zoom only
  const getPageScale = (pageNumber) => {
    const meta = pageMeta[pageNumber];
    if (!meta) return zoom; // Simple zoom-based scaling
    const baseWidth = 600; // Fixed base width
    return (baseWidth * zoom) / meta.origWidth;
  };

  // Handle field placement on a specific page (click-to-place)
  const handlePlaceOnPage = (e, pageNumber) => {
    console.log('🎯 handlePlaceOnPage called:', { pageNumber, selectedRecipient, fieldType });
    
    if (!selectedRecipient) {
      console.warn('❌ Missing recipient:', { selectedRecipient, fieldType });
      alert('Please select a recipient and field type before placing fields on the PDF');
      return;
    }
    
    if (!fieldType) {
      console.warn('❌ Missing field type:', { fieldType });
      alert('Please select a field type before placing fields on the PDF');
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const pageEl = e.currentTarget; // overlay element
    const bounds = pageEl.getBoundingClientRect();
    const clickX = e.clientX - bounds.left;
    const clickY = e.clientY - bounds.top;

    console.log('📍 Click coordinates:', { 
      clickX, 
      clickY, 
      bounds: { width: bounds.width, height: bounds.height },
      pageEl: pageEl.className 
    });

    const scale = getPageScale(pageNumber);
    const defaultW = fieldType === 'signature' ? 150 : 100; // PDF units (72dpi)
    const defaultH = fieldType === 'signature' ? 50 : 30;

    const pdfX = Math.round(clickX / scale);
    const pdfY = Math.round(clickY / scale);

    console.log('📏 Scale calculation:', { scale, pdfX, pdfY, defaultW, defaultH });

    // Unique name per field so DocuSeal stores separate values (fix: duplicate text fields copying same value)
    const fieldId = `field_${Date.now()}`;
    const newField = {
      id: fieldId,
      name: `${fieldType}_${selectedRecipient.name}_${Date.now()}`,
      type: fieldType,
      role: selectedRecipient.name, // DocuSeal uses role to match fields to recipients
      areas: [{ x: pdfX, y: pdfY, w: defaultW, h: defaultH, page: pageNumber }],
      recipient: selectedRecipient
    };

    console.log('✅ Creating new field:', newField);
    setFields(prev => {
      const updated = [...prev, newField];
      console.log('📝 Updated fields array:', updated);
      return updated;
    });
  };

  // Handle field deletion
  const handleDeleteField = (fieldId) => {
    setFields(prev => prev.filter(field => field.id !== fieldId));
  };

  // Zoom controls - ONLY change zoom, no other calculations
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleZoomIn = useCallback(
    debounce(() => {
      console.log('🔍 Manual zoom in');
      setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)));
    }, 100),
    []
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleZoomOut = useCallback(
    debounce(() => {
      console.log('🔍 Manual zoom out');
      setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)));
    }, 100),
    []
  );
  const handleZoomReset = useCallback(() => {
    console.log('🔍 Manual zoom reset');
    setZoom(1);
  }, []);

  // Container width tracking - COMPLETELY DISABLED for static sizing
  // No ResizeObserver, no automatic width calculations
  // PDF uses fixed 600px base width and only scales with manual zoom

  // Page load: cache original page dimensions at scale=1
  const onPageLoadSuccess = (page) => {
    const vp = page.getViewport({ scale: 1 });
    setPageMeta(prev => {
      const existing = prev[page.pageNumber];
      if (existing && existing.origWidth === vp.width && existing.origHeight === vp.height) return prev;
      return { ...prev, [page.pageNumber]: { origWidth: vp.width, origHeight: vp.height } };
    });
  };

  // Fixed page width - NO automatic calculations, only manual zoom
  const pageWidth = useMemo(() => {
    // Always use a fixed base width, only multiply by zoom when user manually changes it
    const baseWidth = 600;
    const finalWidth = Math.floor(baseWidth * zoom);
    console.log('📐 Fixed page width calculation:', { baseWidth, zoom, finalWidth });
    return finalWidth;
  }, [zoom]); // Only depends on zoom, not containerWidth

  // Memoized pages rendering to prevent flicker and hook-in-JSX warning
  const renderedPages = useMemo(() => {
    if (!numPages) {
      console.log('📋 Waiting for pages to load:', { numPages });
      return null;
    }
    return Array.from({ length: numPages }, (_, idx) => (
      <div key={`page_${idx + 1}`} className="pdf-page-wrapper">
        <Page
          pageNumber={idx + 1}
          width={pageWidth}
          onLoadSuccess={onPageLoadSuccess}
          renderAnnotationLayer={false}
          renderTextLayer={false}
          renderMode="canvas"
          loading={<div style={{height:480, background:'#fff'}} />}
        />
        {/* Overlay for fields on this page */}
        <div
          className="page-overlay"
          style={{
            cursor: selectedRecipient && fieldType ? 'crosshair' : 'default'
          }}
          onMouseDown={(e) => {
            // Simpler detection - check if we're clicking on any field-related element
            const isClickingOnField = e.target.closest('.signing-field-element') ||
                                     e.target.closest('[data-field-id]') ||
                                     e.target.getAttribute('data-field-id') ||
                                     e.target.getAttribute('data-field-content');
            
            console.log('🖱️ Page overlay mouse down:', {
              target: e.target.tagName,
              isClickingOnField,
              fieldId: e.target.getAttribute('data-field-id'),
              closestField: !!e.target.closest('.signing-field-element')
            });
            
            // Only place new fields if NOT clicking on existing fields
            if (!isClickingOnField) {
              handlePlaceOnPage(e, idx + 1);
            }
          }}
          role="presentation"
        >
          {fields.filter(f => (f.areas?.[0]?.page || 1) === (idx + 1)).map(field => {
            const area = field.areas[0];
            const scale = getPageScale(idx + 1);
            const left = Math.round(area.x * scale);
            const top = Math.round(area.y * scale);
            const width = Math.round(area.w * scale);
            const height = Math.round(area.h * scale);
            const fieldColor = getRecipientColor(field.recipient?.id);
            return (
              <Rnd
                key={field.id}
                size={{ width, height }}
                position={{ x: left, y: top }}
                bounds={false}
                enableResizing={false} // Disable react-rnd resize handles
                disableDragging={false}
                className="signing-field-element"
                data-field-id={field.id}
                style={{
                  border: `2px solid ${fieldColor.border}`,
                  background: fieldColor.bg,
                  borderRadius: '4px',
                  cursor: 'move',
                  transition: 'none',
                  boxSizing: 'border-box',
                  overflow: 'visible', // Allow our custom handle to show outside
                  position: 'absolute',
                  pointerEvents: 'auto',
                  zIndex: 20
                }}
                dragAxis="both"
                onDragStart={() => {
                  console.log('🎯 Drag started for field:', field.id);
                  // setIsDragging(true);
                }}
                onDragStop={(e, d) => {
                  console.log('🎯 Drag stopped for field:', field.id, {
                    oldPosition: { x: area.x, y: area.y },
                    newPosition: d,
                    scale
                  });
                  // setIsDragging(false);
                  const newPdfX = Math.round(d.x / scale);
                  const newPdfY = Math.round(d.y / scale);
                  
                  console.log('🔄 Drag update:', {
                    fieldId: field.id,
                    before: { x: area.x, y: area.y },
                    after: { x: newPdfX, y: newPdfY }
                  });
                  
                  setFields(prev => {
                    const updated = prev.map(f => {
                      if (f.id === field.id) {
                        return {
                          ...f,
                          areas: [{ ...area, x: newPdfX, y: newPdfY }]
                        };
                      }
                      return f; // Don't modify other fields
                    });
                    console.log('📝 Fields after drag update:', updated.map(f => ({ id: f.id, area: f.areas[0] })));
                    return updated;
                  });
                }}
                // Remove all grid constraints to prevent field interference
                dragGrid={[1,1]}
                // Ensure fields can overlap without collision detection
                enableUserSelectHack={false}
              >
                <div 
                  data-field-content={field.id}
                  style={{
                    padding: '2px 6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: fieldColor.border,
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '2px',
                    textAlign: 'center',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    boxSizing: 'border-box',
                    lineHeight: '1.2',
                    pointerEvents: 'none'
                  }}>
                  {fieldTypes.find(ft => ft.value === field.type)?.icon} {field.recipient?.name || field.role}
                </div>
                {/* Custom resize handle */}
                <div 
                  className="custom-resize-handle"
                  style={{
                    position: 'absolute',
                    bottom: '-6px',
                    right: '-6px',
                    width: '12px',
                    height: '12px',
                    background: 'var(--branding-app-primary)',
                    borderRadius: '50%',
                    border: '1px solid #ffffff',
                    cursor: 'se-resize',
                    zIndex: 100,
                    boxShadow: '0 1px 3px var(--branding-app-primary-ring)',
                    opacity: 0,
                    transition: 'all 0.2s ease',
                    pointerEvents: 'auto'
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    console.log('🔵 Custom resize handle clicked for field:', field.id);
                    // Start custom resize logic
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const startWidth = width;
                    const startHeight = height;
                    
                    const handleMouseMove = (e) => {
                      const newWidth = Math.max(50, startWidth + (e.clientX - startX));
                      const newHeight = Math.max(30, startHeight + (e.clientY - startY));
                      
                      // Update field size in real-time
                      setFields(prevFields => {
                        return prevFields.map(f => {
                          if (f.id === field.id) {
                            return {
                              ...f,
                              areas: [{ 
                                ...f.areas[0], 
                                w: Math.round(newWidth / scale), 
                                h: Math.round(newHeight / scale) 
                              }]
                            };
                          }
                          return f;
                        });
                      });
                    };
                    
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                      console.log('🔵 Custom resize completed for field:', field.id);
                    };
                    
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                />
              </Rnd>
            );
          })}
        </div>
      </div>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPages, pageWidth, fields, fieldTypes, selectedRecipient, fieldType]);

  // Handle template creation with fields
  const handleCreateTemplate = async () => {
    if (fields.length === 0) {
      setError('Please add at least one field to the document');
      return;
    }

    if (!recipients || recipients.length === 0) {
      setError('No recipients found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Extract projectId and documentId from the document data
      const projectId = documentData.projectId;
      const documentId = documentData.id;

      if (!projectId || !documentId) {
        throw new Error('Missing project or document information');
      }

      // Clean up fields for API - remove extra properties and ensure correct structure
      // title: friendly label shown to signer (DocuSeal shows this instead of internal name)
      const typeLabels = { signature: 'Signature', text: 'Text', date: 'Date', initials: 'Initials', checkbox: 'Checkbox' };
      const cleanFields = fields.map(field => ({
        name: field.name,
        type: field.type,
        role: field.role,
        required: true, // Make all fields required by default
        areas: field.areas,
        title: typeLabels[field.type] || (field.type ? field.type.charAt(0).toUpperCase() + field.type.slice(1) : 'Field')
      }));

      // Validate field structure
      cleanFields.forEach((field, index) => {
        if (!field.name || !field.type || !field.role || !field.areas) {
          throw new Error(`Invalid field structure at index ${index}: missing required properties`);
        }
        if (!Array.isArray(field.areas) || field.areas.length === 0) {
          throw new Error(`Invalid field structure at index ${index}: areas must be a non-empty array`);
        }
        field.areas.forEach((area, areaIndex) => {
          if (typeof area.x !== 'number' || typeof area.y !== 'number' || 
              typeof area.w !== 'number' || typeof area.h !== 'number' || 
              typeof area.page !== 'number') {
            throw new Error(`Invalid area structure at field ${index}, area ${areaIndex}: all coordinates must be numbers`);
          }
        });
      });

      console.log('Cleaned and validated fields for API:', cleanFields);

      // Create template with fields
      const templateResult = await docusealService.createTemplateFromPdf(
        projectId,
        documentId,
        cleanFields
      );

      console.log('✅ Template created:', templateResult);

      // Wait for template to be processed before creating submission
      console.log('⏳ Waiting 5 seconds for template to be processed...');
      await new Promise(resolve => setTimeout(resolve, 5000));

      // Now create submission with the template and recipients
      console.log('📤 Creating submission...');
      const submissionResult = await docusealService.createSubmissionWithTemplate(
        projectId,
        documentId,
        templateResult.templateId,
        recipients,
        fields
      );

      console.log('✅ Submission created:', submissionResult);

      // Show success message
      alert(`✅ Template and submission created successfully!\n\nTemplate ID: ${templateResult.templateId}\nSubmission ID: ${submissionResult.submissionId}\nRecipients: ${recipients.length}\n\nDocument is ready for signing. Recipients will receive email notifications.`);
      onClose();
      
    } catch (error) {
      console.error('Error creating template with fields:', error);
      setError(`Failed to create template with fields: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="signing-setup-modal">
      <div className="signing-setup-content">
        <div className="signing-setup-header">
          <div className="header-content">
            <h2>Set up document signing</h2>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="signing-setup-body-wrapper">
        <div className="signing-setup-body">
          <div className="setup-layout">
            {/* Left Panel - PDF Viewer */}
            <div className="pdf-panel">
              <div className="pdf-header">
                <h3>PDF document</h3>
                <div className="pdf-controls">
                  <button className="zoom-btn" onClick={handleZoomOut}>−</button>
                  <span className="zoom-label">{Math.round(zoom * 100)}%</span>
                  <button className="zoom-btn" onClick={handleZoomIn}>+</button>
                  <button className="zoom-reset" onClick={handleZoomReset}>Reset</button>
                </div>
              </div>
              
              {/* Status indicator */}
              <div style={{ 
                padding: '8px 24px', 
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: selectedRecipient && fieldType ? '#f0f9ff' : 'var(--branding-app-surface-panel)',
                fontSize: '14px',
                fontWeight: '500',
                color: selectedRecipient && fieldType ? '#1e40af' : '#6b7280'
              }}>
                {selectedRecipient && fieldType
                  ? `✓ Ready to place ${fieldType} fields for ${selectedRecipient.name}. Click on the PDF to place fields.`
                  : `Select a recipient and field type from the right panel to start placing fields.`
                }
              </div>
              
              <div className="pdf-container" ref={pdfContainerRef}>
                {pdfUrl ? (
                  <div className="pdf-scroll">
                    <Document 
                      file={pdfUrl} 
                      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                      loading={<div style={{padding:12,color:'#64748b'}}>Loading PDF…</div>}
                      error={<div style={{padding:12,color:'#dc2626'}}>Failed to load PDF</div>}
                    >
                      {renderedPages}
                    </Document>
                  </div>
                ) : (
                  <div className="pdf-placeholder">
                    <p>PDF loading...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Controls */}
            <div className="controls-panel">
              {/* Scrollable content wrapper */}
              <div className="controls-scrollable">
              <div className="recipients-section">
                <h3>Recipients ({recipients?.length || 0})</h3>
                <div className="recipients-list">
                  {recipients?.map(recipient => {
                    const recipientColor = getRecipientColor(recipient.id);
                    return (
                      <div 
                        key={recipient.id} 
                        className={`recipient-item ${selectedRecipient?.id === recipient.id ? 'selected' : ''}`}
                        onClick={() => setSelectedRecipient(recipient)}
                        style={{
                          borderLeft: `4px solid ${recipientColor.border}`
                        }}
                      >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div 
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: recipientColor.border,
                            flexShrink: 0
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                            <span className="recipient-name">{recipient.name}</span>
                            {recipient.isPM && <span className="pm-badge">You</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: 'var(--cc-text-muted)' }}>
                            <span className="recipient-email">{recipient.email}</span>
                            {recipient.phone && recipient.phone.trim() !== '' && (
                              <span className="recipient-phone">
                                {recipient.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </div>

              <div className="field-types-section">
                <h3>Field types</h3>
                <div className="field-types-grid">
                  {fieldTypes.map(type => (
                    <button
                      key={type.value}
                      className={`field-type-btn ${fieldType === type.value ? 'selected' : ''}`}
                      onClick={() => setFieldType(type.value)}
                    >
                      <span className="field-icon">{type.icon}</span>
                      <span className="field-label">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="fields-section">
                <h3>Placed fields ({fields.length})</h3>
                <div className="fields-list">
                  {fields.map(field => {
                    const fieldColor = getRecipientColor(field.recipient?.id);
                    return (
                      <div 
                        key={field.id} 
                        className="field-item"
                        style={{
                          borderLeft: `3px solid ${fieldColor.border}`
                        }}
                      >
                        <div className="field-info">
                          <div 
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: fieldColor.border,
                              marginRight: '4px'
                            }}
                          />
                          <span className="field-icon">
                            {fieldTypes.find(ft => ft.value === field.type)?.icon}
                          </span>
                          <span className="field-name">{field.recipient.name}</span>
                          <span className="field-type">{field.type}</span>
                        </div>
                        <button
                          className="delete-field-btn"
                          onClick={() => handleDeleteField(field.id)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                  {fields.length === 0 && (
                    <p className="no-fields">No fields placed yet. Select a recipient and field type, then click on the PDF.</p>
                  )}
                </div>
              </div>

              {/* Error Message - Inside scrollable */}
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              </div>
              {/* End of scrollable wrapper */}
            </div>
          </div>
        </div>
        
        {/* Modal Actions - Moved outside body for always-visible footer */}
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleCreateTemplate}
            disabled={loading || fields.length === 0}
          >
            {loading ? 'Creating template & submission...' : 'Create template & send for signing'}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
} 